import { Link, Outlet, useLocation } from "react-router-dom";
import { StepsNavigation, type NavItem } from "../common/StepsNavigation";
import { StepsFooter } from "../common/StepsFooter";
import Header from "../Header";
import RightAside from "../RightAside";
import UserService from "../../services/UserService";
import { hasTier, isProductAdmin } from "./access-control";
import { TicketSizeToggle } from "./ticket-size-toggle";
import { TicketSizeProvider } from "../../pages/AthenaPricingEngine/lib/ticket-size";

const basePath = "/risk-pricing-engine/state-of-play";

// Every step is an independently reachable page (unlike Underwriting Engine's linear
// wizard), so navigation runs in StepsNavigation/StepsFooter's route mode -- no
// currentStepIndex/onStepChange, no nextBlocked, no isFirstStepHidden (that flag exists
// on UE because its first step advances via its own form-submit, not a generic Next
// click; hiding the footer's Next on step 1 here would leave no way to advance at all).
// The two sub-pages are role-gated (Calibration reviewer+, Manage Alerts admin), so the
// nav is built per-viewer rather than being a module constant. Dropping a child from this
// array is enough for both chrome components: StepsNavigation renders from it, and
// StepsFooter derives its prev/next order from the same array -- so a hidden Calibration
// also stops the footer walking into it from Segments. Numbering and the a/b child
// lettering recompute from whatever survives.
function navItemsFor(canSeeCalibration: boolean, canSeeManageAlerts: boolean): NavItem[] {
  // Built as a list, not a single ternary: Loss Calibration and Annualized Loss are
  // both gated on canSeeCalibration, but Weighted Average Life has no such gate (same
  // access as Segments itself) and must not disappear along with the other two for a
  // viewer who lacks reviewer+. For a reviewer+ viewer this gives the intended
  // "a" = Loss Calibration, "b" = Annualized Loss, "c" = Weighted Average Life; for
  // anyone else, Weighted Average Life alone becomes "a" -- a correct consequence of
  // the other two being hidden, not a bug.
  const segmentsChildren: NavItem[] = [
    ...(canSeeCalibration
      ? [
          { label: "Loss Calibration", path: "/risk-pricing-engine/calibration" },
          // Same reviewer+ gate as Loss Calibration -- reads the same
          // annualized_loss_calibiration data domain.
          { label: "Annualized Loss", path: "/risk-pricing-engine/annualized-loss" },
        ]
      : []),
    { label: "Weighted Average Life", path: "/risk-pricing-engine/weighted-average-life" },
  ];
  const segments: NavItem = {
    label: "Segments",
    path: "/risk-pricing-engine/segments",
    children: segmentsChildren,
  };
  const exceptions: NavItem = {
    label: "Exceptions",
    path: "/risk-pricing-engine/exceptions",
    ...(canSeeManageAlerts
      ? { children: [{ label: "Manage Alerts", path: "/risk-pricing-engine/manage-alerts" }] }
      : {}),
  };

  return [
    { label: "State Of Play", path: "/risk-pricing-engine/state-of-play" },
    segments,
    exceptions,
    { label: "Planning", path: "/risk-pricing-engine/planning" },
    { label: "Manage Targets", path: "/risk-pricing-engine/manage-targets" },
    { label: "Snapshot", path: "/risk-pricing-engine/snapshot" },
    { label: "Discipline", path: "/risk-pricing-engine/discipline" },
  ];
}

// Pages whose data the Ticket Size toggle filters: every table the pipeline writes carries a ticket_size label.
// The rest have nothing to filter, so on those the toggle is shown disabled: Snapshot (embedded Superset
// dashboards the app doesn't query), Manage Alerts (rule thresholds) and Manage Access (roles).
const TICKET_SIZE_PAGES = [
  "/risk-pricing-engine/state-of-play",
  "/risk-pricing-engine/segments",
  "/risk-pricing-engine/calibration",
  "/risk-pricing-engine/annualized-loss",
  "/risk-pricing-engine/weighted-average-life",
  "/risk-pricing-engine/exceptions",
  "/risk-pricing-engine/planning",
  "/risk-pricing-engine/manage-targets",
  "/risk-pricing-engine/discipline",
];

export function AppLayout() {
  const token = UserService.getToken();
  const { pathname } = useLocation();
  const ticketSizeApplies = TICKET_SIZE_PAGES.some((page) => pathname.startsWith(page));
  const navItems = navItemsFor(hasTier("reviewer"), hasTier("admin"));
  const showManageAccess = isProductAdmin();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header
        token={token}
        isMarketplacePage={false}
        notificationsCount={0}
        refreshNotifications={() => {}}
        setIndustry={() => {}}
        setCompany={() => {}}
        leftNode={null}
        productLabel="Risk Based Pricing Engine"
        fixed={true}
      />
      <StepsNavigation navItems={navItems} basePath={basePath} />

      <TicketSizeProvider>
        <main className="flex-1 p-6 animate-fade-in pt-[200px] pb-[80px]">
          {/* Same right-aligned row underwriting-engine puts at the top of its main content
              (UnderwritingEngineV2/AppLayout.tsx). The Ticket Size toggle sits here on every
              page; Manage Access shares the row for admins. */}
          <div className="flex items-center justify-end gap-6 mb-3">
            <TicketSizeToggle disabled={!ticketSizeApplies} />
            {showManageAccess && (
              <Link
                to="/risk-pricing-engine/manage-access"
                className="text-xs font-medium text-amber hover:underline"
              >
                Manage Access →
              </Link>
            )}
          </div>
          <Outlet />
        </main>
      </TicketSizeProvider>

      <StepsFooter navItems={navItems} basePath={basePath} />
      <RightAside showChildrenWhenNoPanel={false}>{null}</RightAside>
    </div>
  );
}
